// ink-component
import React, { ReactNode, useContext, useEffect } from 'react';
import debounce from 'lodash/debounce';

import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import Bubble from '../../components/Bubble/Bubble';

import { bemHelper, injectChild } from '../../helpers';
import useClickOutside from '../../hooks/useClickOutside';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';

import getGlobalProps from '../../helpers/constants/global-props';
import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';

import TableContext from '../../components/Table/TableContext/TableContext';

export type ChildType = {
  props: {
    onClick: () => void;
  };
};

export interface DeprecatedDropdownProps {
  /** Trigger unique identifier */
  id?: string;
  /** href */
  href?: string;
  /** Trigger size modifier */
  size?: 'mini' | 'small' | 'wide' | 'full';
  /** Type modifier for the trigger button */
  type?: 'aura' | 'link' | 'danger' | 'actions' | 'primary' | 'inverse' | 'transparent';
  /** Modifier for aligning the dropdown box to the right of the trigger */
  align?: 'right';
  /** Modifies the dropdown button to be a cell button. */
  isCell?: boolean;
  /** Contents that will be wrapped in the trigger button */
  trigger?: ReactNode;
  /** Optional callback for the trigger */
  onClick?: (e: Event) => void;
  /** Size of the dropdown box content */
  boxSize?: 'large';
  /** onChange callback, passes the boolean value that represents if the Dropdown is opened */
  onChange?: ({ isOpen: boolean }) => void;
  /** The actual list of contents of your dropdown.
   * Usually wrapped inside a [DropdownItem](#dropdown-item). */
  children?: any;
  /** Size modifier for the trigger button */
  /** Optional arrow down icon */
  hasArrow?: boolean;
  /** Modifier for disabled state for the trigger button */
  isDisabled?: boolean;
  /** Used to prevent the Dropdown to close when an item is selected */
  preventClose?: boolean;
  /** Optional aditional props for the trigger contents */
  triggerProps?: any;
  /** When the dropdown should be positioned relative to another wrapper. */
  removeRelative?: boolean;
  /** Fixed items at the top of the dropdown */
  fixedItems?: ReactNode;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
  /** @ignore */
  isStaff?: boolean;
  /** @ignore */
  isNavItem?: boolean;
  /** @ignore */
  isActive?: boolean;
}

const globalProps = getGlobalProps({ deprecated: true });

/**
 * @deprecated This component is deprecated and replaced by [Dropdown](https://ink.carta.com/components/Dropdown/)
 * @status deprecated
 * @replacementComponent Dropdown
 * @dateOfDeletion July 1st, 2023
 */

function DeprecatedDropdown(props: DeprecatedDropdownProps) {
  const {
    id,
    href,
    size,
    type,
    align = '',
    isCell,
    isStaff,
    trigger,
    boxSize = '',
    onClick,
    children,
    isActive,
    hasArrow,
    onChange,
    isNavItem,
    fixedItems,
    isDisabled,
    preventClose,
    triggerProps,
    removeRelative,
  } = props;
  useDeprecatedWarning({ componentName: 'DeprecatedDropdown', substitute: 'Dropdown' });

  const { tableId, hasScroll } = useContext(TableContext);

  const onClickOutside = () => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    onChange && onChange({ isOpen: open });
  };

  // useEnforceChildren(children, 'Dropdown.Item');
  const [open, setOpen, ref] = useClickOutside(onClickOutside);

  useDidUpdateEffect(() => {
    onClickOutside();
  }, [open]);

  useEffect(() => {
    /* Dropdowns should close whenever the Table they are inside scrolls horizontally. */
    if (tableId && hasScroll && open) {
      const table = document.getElementById(tableId);
      const scrollWrapper = table && table.closest('.wrapper-ink-table--scroll');
      // Adds event listener to close in the case of a horizontal scroll (with a delay)
      if (scrollWrapper)
        scrollWrapper.addEventListener(
          'scroll',
          debounce(() => setOpen(false), 500),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, hasScroll, open]);

  const defineWrapperClassName = () => {
    const b = bemHelper();

    return b('wrapper-dropdown', {
      [align]: Boolean(align),
      static: removeRelative || hasScroll || isNavItem,
    });
  };

  const defineClassName = () => {
    const b = bemHelper();

    return b('ink-dropdown', {
      open,
      [boxSize]: Boolean(boxSize),
    });
  };

  const defineNavItemClassName = () => {
    const b = bemHelper();

    return b('ink-nav--item', {
      active: isActive,
    });
  };

  const toggleDropdown = e => {
    e.stopPropagation();
    setOpen(open => !open);
    onClick && onClick(e);
  };

  const handleKeyPress = e => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.keyCode === 13) {
      toggleDropdown(e);
    }
  };

  const renderArrow = () => {
    return (
      hasArrow && (
        <>
          &nbsp;
          <Icon type="down-bold" size="sm" meaning="Open dropdown" />
        </>
      )
    );
  };

  const renderStaffBubble = () => {
    return (
      isStaff && (
        <>
          &nbsp;<Bubble type="staff">Staff</Bubble>
        </>
      )
    );
  };

  const renderTrigger = () => {
    const Trigger = trigger;
    return typeof Trigger === 'function' ? <Trigger {...triggerProps} /> : trigger;
  };

  const renderChildren = () => {
    return React.Children.map(children, (child: ChildType) => {
      const onClick = () => {
        if (child && child.props.onClick) child.props.onClick();
        if (!preventClose) {
          setOpen(false);
        }
      };
      return injectChild(child, { onClick });
    });
  };

  const handleOnBlur = ({ currentTarget }) => {
    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        setOpen(false);
      }
    }, 0);
  };

  return (
    <div
      data-testid={props['data-testid']}
      aria-label="menu"
      ref={ref}
      className={defineWrapperClassName()}
      onBlur={handleOnBlur}
      {...globalProps}
    >
      {isNavItem ? (
        <a
          tabIndex={0}
          onKeyPress={handleKeyPress}
          id={id}
          onClick={toggleDropdown}
          className={defineNavItemClassName()}
          href={href}
          {...getGlobalProps({ deprecated: true })}
        >
          {renderTrigger()}
          {renderStaffBubble()}
          {renderArrow()}
        </a>
      ) : (
        <Button
          id={id}
          onClick={toggleDropdown}
          size={size}
          type={type}
          isCell={isCell}
          isDisabled={isDisabled}
          {...getGlobalProps({ deprecated: true })}
        >
          {renderTrigger()}
          {renderArrow()}
        </Button>
      )}
      <div className={defineClassName()}>
        {Boolean(fixedItems) && <ul className="ink-dropdown--list">{fixedItems}</ul>}
        <ul role="menu" className="ink-dropdown--list ink-dropdown--list__scrollable">
          {renderChildren()}
        </ul>
      </div>
    </div>
  );
}

DeprecatedDropdown.defaultProps = {
  id: null,
  trigger: null,
  triggerProps: null,
  hasArrow: false,
  onClick: () => {},
  onChange: () => {},
  size: 'auto',
  type: 'default',
  align: null,
  isDisabled: false,
  children: null,
  preventClose: false,
  isCell: false,
  removeRelative: false,
  boxSize: null,
  isNavItem: false,
  href: null,
  isStaff: false,
  isActive: false,
  fixedItems: null,
  'data-testid': undefined,
};

DeprecatedDropdown.displayName = 'DeprecatedDropdown';

export default DeprecatedDropdown;
